import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  TextField,
  Checkbox,
  Typography,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from "@mui/material/Grid2";
import { get } from "../../../services/apiService";
import BackdropLoader from "../../common/BackdropLoader";
import i18next from "i18next";
import { useAuth } from "../../../hooks/useAuth";

const SectionForm = ({ screenWidth, onSectionContinue }) => {
  const [sections, setSections] = useState([]);
  const { currentLang } = useAuth();
  // Removed unused apiSections state
  const [layoutOptions, setLayoutOptions] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState({ label: "", id: "" });
  const [isLoading, setIsLoading] = useState(true);
  const userId = sessionStorage.getItem("userId");

  const handleAddSection = () => {
    const newSection = {
      name: `custom-${sections?.length}`,
      value: "",
      checked: false,
      isCustom: true,
      id: null,
      translation: [
        { languageCode: "en", languageText: "" },
        { languageCode: "ar", languageText: "" },
      ],
    };

    setSections([
      ...sections.slice(0, -1),
      newSection,
      ...sections.slice(-1),
    ]);
  };

  const handleDeleteSection = (index) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setSections(updatedSections);
  };

  const handleInputChange = (index, event) => {
    const updatedSections = [...sections];
    const translationIndex = updatedSections[index].translation.findIndex(
      (t) => t.languageCode === currentLang
    );

    if (translationIndex !== -1) {
      updatedSections[index].translation[translationIndex].languageText =
        event.target.value;
    }

    setSections(updatedSections);
  };

  const handleCheckboxChange = (index) => {
    const updatedSections = sections.map((section, i) => {
      if (i === index) {
        return { ...section, checked: !section.checked };
      }
      return section;
    });

    setSections(updatedSections);
    // Removed unused setApiSections call
  };

  const isAnySelected = sections.some(
    (section) =>
      section.checked &&
      section.translation?.find((t) => t.languageCode === currentLang)?.languageText?.trim() !== ""
  );

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  // Wrap getSections in useCallback to include it in useEffect dependencies
  const getSections = useCallback(async () => {
    const response = await get(
      `users/${userId}/section/list`,
      {
        headers: {
          "Accept-Language": currentLang,
        },
      },
      onLoading
    );
    const customerSections = response?.data?.map((section) => ({
      id: section?.section?.id,
      value: section?.sectionName,
      checked: true,
      name: section?.sectionName?.toLowerCase().replace(/\s+/g, "-"),
      translation: section.translation,
    }));

    let contentSection = response?.data?.filter(
      (section) =>
        section.sectionName !== "Header" && section.sectionName !== "Footer"
    );

    contentSection = contentSection?.map((section) => ({
      id: section?.section?.id,
      value: section?.sectionName,
      checked: true,
      name: section?.sectionName?.toLowerCase().replace(/\s+/g, "-"),
      translation: section.translation,
    }));

    if (customerSections) {
      setLayoutOptions(contentSection);
      setSections(customerSections);
    }
  }, [userId, currentLang]); // Add dependencies

  const handleChange = (event) => {
    const requiredId = event.target.value;
    const requiredSection = layoutOptions.find(
      (section) => section.id === requiredId
    );

    if (!requiredSection) {
      console.error("Required section not found!");
      return;
    }

    const updatedSections = sections.map((section, index) => {
      const isSecondLastIndex = index === sections.length - 2;
      if (isSecondLastIndex) {
        return {
          ...section,
          id: requiredId,
          name: requiredSection.name,
          translation: JSON.parse(JSON.stringify(requiredSection.translation)),
        };
      }
      return section;
    });

    setSections(updatedSections);
    setSelectedLayout({ id: requiredId, label: requiredSection.value });
  };

  const handleContinue = () => {
    const formattedPayload = sections.map((section) => ({
      id: section.id,
      checked: section.checked,
      name:
        section.name ||
        section.translation
          ?.find((t) => t.languageCode === currentLang)
          ?.languageText?.toLowerCase()
          .replace(/\s+/g, "-"),
      translation: section.translation.map((t) => ({
        languageCode: t.languageCode,
        languageText: t.languageText,
      })),
    }));
    onSectionContinue(formattedPayload);
  };

  useEffect(() => {
    getSections();
  }, [getSections]); // Add getSections to dependency array

  return (
    <Grid
      container
      spacing={3}
      justifyContent="center"
      alignItems="center"
      sx={{ padding: screenWidth > 800 ? "3% 20%" : "3% 3%" }}
    >
      {isLoading && <BackdropLoader />}
      <Grid item size={{ xs: 12 }} sx={{ marginBottom: "5%" }}>
        <Typography
          variant="h4"
          align="center"
          sx={{ fontFamily: "Tajawal-Bold", fontSize: "40px", color: "black" }}
        >
          {i18next.t("SECTIONS_PAGE_HEADING_1")}
        </Typography>
      </Grid>

      <Grid item size={{ xs: 12 }} textAlign="right">
        <IconButton
          onClick={handleAddSection}
          sx={{
            height: "55px",
            width: "157px",
            border: "1px solid #575452",
            borderRadius: "10px",
            padding: "5px 10px",
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              color: "#575452",
              fontFamily: "Tajawal-Regular",
            }}
          >
            {i18next.t("SECTIONS_PAGE_BUTTON_LABEL_CUSTOM")}
          </Typography>
          <AddIcon />
        </IconButton>
      </Grid>

      {sections &&
        sections.map((section, index) => (
          <Grid
            key={index}
            container
            item
            size={{ xs: 12 }}
            alignItems="center"
            spacing={2}
          >
            <Grid item size={{ xs: 9 }} padding={1} sx={{ marginLeft: "-3%" }}>
              <TextField
                variant="standard"
                fullWidth
                value={
                  section?.translation?.find(
                    (t) => t.languageCode === currentLang
                  )?.languageText || ""
                }
                onChange={(e) => handleInputChange(index, e)}
                sx={{
                  fontFamily: "Tajawal-Regular",
                  fontSize: "24px",
                  color: "#575452",
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#575452",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#575452",
                  },
                }}
              />
            </Grid>

            {section.isCustom ? (
              <Grid
                item
                size={{ xs: 3 }}
                textAlign="center"
                sx={{ display: "flex" }}
              >
                <Select
                  value={selectedLayout.id}
                  onChange={handleChange}
                  label="Select a Layout"
                  fullWidth
                >
                  {layoutOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
                <Checkbox
                  checked={section.checked}
                  onChange={() => handleCheckboxChange(index)}
                  sx={{
                    color: "black",
                    "&.Mui-checked": { color: "black" },
                    width: "50px",
                    height: "50px",
                    marginLeft: {
                      xl: "36%",
                      lg: "30%",
                      md: "23%",
                      sm: "30%",
                      xs: "12%",
                    },
                  }}
                />
                <IconButton onClick={() => handleDeleteSection(index)}>
                  <DeleteIcon sx={{ color: "black" }} />
                </IconButton>
              </Grid>
            ) : (
              <Grid item size={{ xs: 3 }} textAlign="center">
                <Checkbox
                  checked={section.checked}
                  onChange={() => handleCheckboxChange(index)}
                  disabled={!section.isCustom}
                  sx={{
                    color: !section.isCustom ? "grey" : "black",
                    "&.Mui-checked": {
                      color: !section.isCustom ? "grey" : "black",
                    },
                    width: "50px",
                    height: "50px",
                  }}
                />
              </Grid>
            )}
          </Grid>
        ))}

      {isAnySelected && (
        <Grid item size={{ xs: 12 }} textAlign="center" sx={{ marginTop: "7%" }}>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{
              background:
                "linear-gradient(to right, #004D8A, #02599C, #0886E1, #0A91F2)",
              color: "#F0F0F0",
              fontSize: screenWidth > 600 ? "32px" : "24px",
              fontFamily: "Tajawal-Bold",
              padding: "8px 20px",
              width: screenWidth > 600 ? "30%" : "50%",
              borderTopLeftRadius: "15px",
              borderBottomRightRadius: "15px",
            }}
          >
            {i18next.t("SECTIONS_PAGE_BUTTON_LABEL_CONTINUE")}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default SectionForm;